import {
  allActivity,
  logStudy,
  replaceActivity,
  resetActivity,
  todayKey,
} from "@/hooks/useActivity";
import {
  allMastery,
  recordMastery,
  replaceMastery,
  resetMastery,
  type MasteryMap,
} from "@/hooks/useMastery";
import {
  allSrsEntries,
  dropRecord,
  getRecord,
  gradeRecord,
  replaceSrs,
  resetSrs,
  seedRecord,
  srsKey,
  type SrsMap,
} from "@/hooks/useSrs";
import { orderByPriority } from "./queue";
import { DAY_MS, boxFromMastery, masteryFromBox, type Grade, type SrsRecord } from "./srs";
import type { SyncKind } from "./merge";
import { knownMetaKeys, markDirty, markManyDirty, withRemoteApply } from "./syncState";
import type { RawActivityMap } from "./activity";
import type { Mastery, Word } from "@/types/lex";

/**
 * The one place a "study event" is applied. Every screen that judges an
 * answer goes through here so the three stores can never drift apart:
 *
 *   schedule (srs) → ledger label (mastery) → daily activity (streak, goal)
 *
 * Screens must not write to those stores directly.
 */

/** Drills judge answers binary; the Review screen offers the full four. */
export function gradeFromCorrect(correct: boolean): Grade {
  return correct ? "good" : "again";
}

/**
 * The words a drill round should ask, weakest first: overdue, then unseen,
 * then still-learning, then mastered. Truncated to `size`, so a short round
 * spends its questions where they're worth the most.
 */
export function studyQueue(
  day: number,
  words: readonly Word[],
  size: number,
  now = Date.now()
): Word[] {
  const ordered = orderByPriority(words, (w) => getRecord(day, w.index), now);
  return ordered.slice(0, Math.max(0, Math.min(size, ordered.length)));
}

/**
 * Apply one graded answer: a drill result, a flashcard verdict, or a rating
 * from the Review screen.
 */
export function review(day: number, index: number, grade: Grade, now = Date.now()): void {
  const firstTime = getRecord(day, index) == null;
  const record = gradeRecord(day, index, grade, now);
  recordMastery(day, index, masteryFromBox(record.box));
  logStudy({ learned: firstTime }, now);
  flagEdit(day, index, now);
}

/**
 * Apply a hand-picked Unknown / Learning / Known label. Seeds a schedule so
 * the word joins the review rotation instead of sitting outside it, and
 * clearing the label removes it from the rotation again.
 */
export function mark(day: number, index: number, value: Mastery, now = Date.now()): void {
  if (value === "untouched") {
    dropRecord(day, index);
    recordMastery(day, index, value);
    // Deleting is an edit like any other: the record is now absent locally, so
    // the next push emits it as a tombstone and the other devices drop it too.
    markDirty("srs", srsKey(day, index), now);
    return;
  }
  const firstTime = getRecord(day, index) == null;
  seedRecord(day, index, value, now);
  recordMastery(day, index, value);
  logStudy({ learned: firstTime }, now);
  flagEdit(day, index, now);
}

/**
 * Flag the two records a study event touches. Mastery is deliberately absent —
 * it is derived from the schedule, so syncing it would only create a way for
 * the label and the box to disagree.
 */
function flagEdit(day: number, index: number, now: number): void {
  markManyDirty(
    [
      { kind: "srs", key: srsKey(day, index) },
      { kind: "activity", key: todayKey(now) },
    ],
    now
  );
}

/**
 * FNV-1a, used only to spread seeded review dates deterministically.
 */
function hash32(text: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < text.length; i += 1) {
    h ^= text.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return h >>> 0;
}

/**
 * The schedule that a set of hand-picked labels implies. Pure, and — the part
 * that matters — deterministic.
 *
 * Known words are spread over the next fortnight instead of landing in one
 * pile, but the spread is a hash of the word's key rather than `Math.random`.
 * Seeded rows are derived state, so they are never pushed; if each device
 * rolled its own dice, the same account would show a different number of due
 * cards on every device, forever.
 */
export function planSeed(
  mastery: Readonly<MasteryMap>,
  srs: Readonly<SrsMap>,
  now: number
): SrsMap {
  const additions: SrsMap = {};
  for (const [key, label] of Object.entries(mastery)) {
    if (srs[key] != null) continue;
    const box = boxFromMastery(label);
    // Anything not yet mastered becomes due immediately — that matches what
    // the old Review screen showed.
    const due = label === "known" ? now + (1 + (hash32(key) % 14)) * DAY_MS : now;
    additions[key] = { box, due, last: 0, reps: 0, lapses: 0 } satisfies SrsRecord;
  }
  return additions;
}

/**
 * Give a schedule to words that were labelled before the SRS existed.
 *
 * Idempotent — only labels without a schedule are seeded — so it is safe to
 * run on every boot and again after every pull. On a full corpus this touches
 * 1,200 entries in a single commit.
 */
export function seedFromMastery(now = Date.now()): number {
  const existing = allSrsEntries();
  const additions = planSeed(allMastery(), existing, now);
  const seeded = Object.keys(additions).length;
  // Derived, not authored: seeding must never queue a push.
  if (seeded > 0) withRemoteApply(() => replaceSrs({ ...existing, ...additions }));
  return seeded;
}

/**
 * The ledger label implied by a schedule.
 *
 * Mastery is not stored independently of the SRS box — every writer above
 * keeps the two in lockstep, and `masteryFromBox(boxFromMastery(v)) === v` for
 * all three labels. Treating it as derived is what lets sync carry one record
 * per word instead of two, and removes any way for the label to contradict the
 * box after a partial merge.
 */
export function deriveMastery(srs: Readonly<SrsMap>): MasteryMap {
  const out: MasteryMap = {};
  for (const [key, record] of Object.entries(srs)) {
    out[key] = masteryFromBox(record.box);
  }
  return out;
}

/** Re-point the ledger at whatever the schedule now says. */
export function refreshMastery(): void {
  withRemoteApply(() => replaceMastery(deriveMastery(allSrsEntries())));
}

/**
 * Wipe all progress, everywhere.
 *
 * Clearing the stores alone would only be local, and the next pull would hand
 * everything straight back. So every key is flagged first, while it is still
 * known; with the values then gone, the next push emits each one as a
 * tombstone. Keys already carrying metadata are included so that a record
 * tombstoned by an earlier reset stays tombstoned.
 *
 * The flags live in localStorage, so a reset performed offline survives a
 * restart and drains when the device next reaches the network.
 */
export function resetProgress(now = Date.now()): void {
  const seen = new Set<string>();
  const keys: { kind: SyncKind; key: string }[] = [];
  const add = (kind: SyncKind, key: string) => {
    const id = `${kind}/${key}`;
    if (seen.has(id)) return;
    seen.add(id);
    keys.push({ kind, key });
  };

  for (const key of Object.keys(allSrsEntries())) add("srs", key);
  for (const key of Object.keys(allActivity())) add("activity", key);
  for (const mk of knownMetaKeys()) {
    const sep = mk.indexOf("/");
    if (sep > 0) add(mk.slice(0, sep) as SyncKind, mk.slice(sep + 1));
  }

  markManyDirty(keys, now);
  resetSrs();
  resetActivity();
  resetMastery();
}

/** The payload `exportProgress` writes and `importProgress` accepts. */
export interface ProgressExport {
  version: number;
  exportedAt: string;
  mastery: MasteryMap;
  srs: SrsMap;
  activity: RawActivityMap;
}

/**
 * Restore a progress export, replacing whatever is here now.
 *
 * Every restored key is flagged for the next push: an import is an explicit
 * act by the learner and should win against whatever the account currently
 * holds. `mastery` in the file is ignored and re-derived — it is redundant
 * with the schedule, and trusting it would let a hand-edited file put the
 * label and the box out of step.
 *
 * Activity rows exported before per-device buckets existed carry a bare
 * `YYYY-MM-DD` key. They are kept as-is rather than claimed by this device:
 * a shared key merges by last-write-wins, whereas rewriting it would let the
 * same file imported on two devices count itself twice.
 */
export function importProgress(payload: unknown, now = Date.now()): number {
  const data = payload as Partial<ProgressExport> | null;
  if (data == null || typeof data !== "object") {
    throw new Error("Not a progress file");
  }
  const srs = (data.srs ?? {}) as SrsMap;
  const activity = (data.activity ?? {}) as RawActivityMap;
  if (typeof srs !== "object" || typeof activity !== "object") {
    throw new Error("Progress file is malformed");
  }

  withRemoteApply(() => {
    replaceSrs(srs);
    replaceActivity(activity);
    replaceMastery(deriveMastery(srs));
  });

  const keys: { kind: SyncKind; key: string }[] = [
    ...Object.keys(srs).map((key) => ({ kind: "srs" as const, key })),
    ...Object.keys(activity).map((key) => ({ kind: "activity" as const, key })),
  ];
  markManyDirty(keys, now);
  return Object.keys(srs).length;
}
